import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import getUser from '@/endpoints/itasca/users/getUser';
import { useAuth } from '@/providers/auth-provider';

type UserQuotasContextType = {
  loading: boolean;
  refreshQuotas: (signal: AbortSignal) => Promise<void>;
  reports: Quota | null;
  savedselections: Quota | null;
  userCanGenerateReport: boolean;
  userCanSaveSelection: boolean;
};

type Quota = {
  count: number;
  limit: number | null;
};

const UserQuotasContext = createContext<UserQuotasContextType>({
  loading: false,
  refreshQuotas: async () => {},
  reports: null,
  savedselections: null,
  userCanGenerateReport: false,
  userCanSaveSelection: false,
});

export const UserQuotasProvider: FC<PropsWithChildren> = ({
  children = null,
}) => {
  const { isAnonymous, userDjangoId, authStateLoading } = useAuth();

  const [reports, setReports] = useState<Quota | null>(null);
  const [userCanGenerateReport, setUserCanGenerateReport] =
    useState<boolean>(false);
  const [savedselections, setSavedSelections] = useState<Quota | null>(null);
  const [userCanSaveSelection, setUserCanSaveSelection] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getUserQuota = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      try {
        if (isAnonymous || !userDjangoId || authStateLoading) {
          setUserCanGenerateReport(false);
          return;
        }

        const { res } = await getUser(userDjangoId, signal);

        if (!res) {
          setUserCanGenerateReport(false);
          return;
        }

        const quotas = res?.data.active_organization.quotas;
        const { count, limit } = quotas['insights-report'];

        if (
          limit === undefined ||
          limit === null ||
          typeof limit !== 'number'
        ) {
          setUserCanGenerateReport(true);
        } else {
          setUserCanGenerateReport(count < limit);
        }

        setReports({
          count,
          limit: limit ?? null,
        });

        const { count: selectionCount, limit: selectionLimit } =
          quotas.savedselection;

        if (
          selectionLimit === undefined ||
          selectionLimit === null ||
          typeof limit !== 'number'
        ) {
          setUserCanSaveSelection(true);
        } else {
          setUserCanSaveSelection(selectionCount < selectionLimit);
        }

        setSavedSelections({
          count: selectionCount,
          limit: selectionLimit ?? null,
        });
      } finally {
        setLoading(false);
      }
    },
    [authStateLoading, isAnonymous, userDjangoId]
  );

  useEffect(() => {
    const abortController = new AbortController();
    getUserQuota(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [getUserQuota]);

  const value = useMemo(
    () => ({
      loading,
      refreshQuotas: getUserQuota,
      reports,
      savedselections,
      userCanGenerateReport,
      userCanSaveSelection,
    }),
    [
      getUserQuota,
      loading,
      reports,
      savedselections,
      userCanGenerateReport,
      userCanSaveSelection,
    ]
  );

  return (
    <UserQuotasContext.Provider value={value}>
      {children}
    </UserQuotasContext.Provider>
  );
};

export const useUserQuotasContext = () => useContext(UserQuotasContext);
